import React, { Component } from 'react';
import {
  NormalButton,
  LockableButtonGroup,
} from '../components';
import {
  inject,
  observer,
} from 'mobx-react';
import { OUTPUT_TTL } from '../../../constants/Pac';
import { DeviceStatus } from '../../../constants/DeviceStatus';
import { DeviceTypes } from '../../../constants/DeviceModels';
import {
  Sound,
  Sounds,
} from '../../Sound';
import Counter, { formatSeconds } from '../../Counter/Counter';
import Toggle from 'react-toggle';
import { IconUnitLabel } from '../components/labels';
import {
  AnalogIconList,
  AnalogUnitList,
} from '../../../constants/DeviceModels';
import LockIcon from '../components/LockableButtonGroup/LockIcon';
import './CentralMixer.css';
import logger from '../../../utils/logger';
import { mixerSwitch } from '../../../stores/batch/device-switches/mixer-switch';

@inject('batchStore', 'devicesStore')
@observer
class CentralMixer extends Component {

  constructor(props) {
    super(props);
    this.state = {
      lockModeEnabled: props.device.locked || false,
    };
    this.initialLockState = props.device.locked || false;
  }

  componentDidUpdate(prevProps) {
    // Sync state with device prop if it changes from backend
    if (prevProps.device.locked !== this.props.device.locked) {
      const newLockState = this.props.device.locked || false;
      this.setState({ lockModeEnabled: newLockState });

      // Also update the LockableButtonGroup's lock state
      if (this.lockableButtonToggle) {
        this.lockableButtonToggle(newLockState);
      }
    }
  }

  handleDeviceLockToggle = () => {
    const { device, devicesStore } = this.props;
    const newLockState = !this.state.lockModeEnabled;

    // Update local state immediately
    this.setState({ lockModeEnabled: newLockState });

    // Update the LockableButtonGroup's lock mode
    if (this.lockableButtonToggle) {
      this.lockableButtonToggle(newLockState);
    }

    // Update backend
    devicesStore.updateDevice(device.id, DeviceTypes.Mixer, {
      ...device,
      locked: newLockState,
    }).catch(error => {
      logger.error('Failed to update device lock state:', error);
    });
  };

  handleLockStateChange = (lockModeEnabled, toggleLockMode) => {
    this.lockableButtonToggle = toggleLockMode;

    // On first render, sync the backend state to the LockableButtonGroup
    if (this.initialLockState !== undefined) {
      toggleLockMode(this.initialLockState);
      this.initialLockState = undefined; // Only sync once
    }

    if (lockModeEnabled !== this.state.lockModeEnabled) {
      this.setState({ lockModeEnabled });
    }
  };

  render() {
    const { device, batchStore, devicesStore } = this.props;
    const { lockModeEnabled } = this.state;
    const isBarrelFullyTilted = !batchStore.isInputPortOff(device.openTiltStatusPNo);
    const isBarrelReturned = !batchStore.isInputPortOff(device.closeReturnStatusPNo);
    let mixerDataStatus = batchStore.getDeviceStatus(device.id);
    const { uid, tId, currentDrop, allDrops, truckCode, active } = mixerDataStatus;
    const isActive = batchStore.isMixerOn(device) ? 'active' : '';
    const isOn = !!isActive || !!active;
    const name = device.description || `C Mixer #${device.id}`;
    const error = mixerDataStatus.error ? 'error' : '';
    let gate = '', countDownOn = mixerDataStatus.status !== DeviceStatus.IDLE;
    const mCode = uid ? `${truckCode || tId || uid} ${currentDrop}/${allDrops}` : '';
    const inputDisabled = batchStore.isInputDisabled(device.openTiltStatusPNo) || batchStore.isInputDisabled(device.closeReturnStatusPNo)
      ? 'device-input-disabled' : '';
    const isDisabled = device.diverterPNo ? batchStore.isOutputOn(device.diverterPNo) : false;
    const disabled = isDisabled ? 'disabled' : '';
    const analogInput = device.analogInputId ? devicesStore.devices.find(v => v.id === device.analogInputId) || {} : {};

    if (isBarrelFullyTilted && !isBarrelReturned) {
      gate = 'gate-open';
    } else if (!isBarrelFullyTilted && !isBarrelReturned) {
      gate = 'gate-half-open';
    } else if (!isBarrelFullyTilted && isBarrelReturned) {
      gate = 'gate-close';
    } else if (isBarrelFullyTilted && isBarrelReturned) {
      gate = 'device-error';
    }

    const onCountDown = (data) => {
      mixerDataStatus = batchStore.getDeviceStatus(device.id);
      let s = {
        ...mixerDataStatus,
        currentCount: data.seconds,
        restart: false,
        active: isOn,
      };

      if (mixerDataStatus.status === DeviceStatus.MIXING && isBarrelFullyTilted && !isBarrelReturned) {
        s.status = DeviceStatus.TOP_OPEN;
        s.delay = device.topOpenTime;
        s.restart = true;
      } else if (mixerDataStatus.status === DeviceStatus.TOP_OPEN && data.seconds >= 0 && !mixerDataStatus.restart) {
        s.status = DeviceStatus.RETURNING;
        s.delay = 0;
        s.restart = true;
      } else if (mixerDataStatus.status === DeviceStatus.RETURNING && isBarrelReturned) {
        s.status = DeviceStatus.CLOSE_DELAY;
        s.delay = device.downCloseDelay;
        s.restart = true;
      } else if (mixerDataStatus.status === DeviceStatus.CLOSE_DELAY && data.seconds >= 0 && !mixerDataStatus.restart) {
        s.status = DeviceStatus.FINISH;
        s.restart = false;
        s.currentCount = 0;
      }

      batchStore.deviceStatus.set(device.id, s);
    };

    return (
      <div id={`device-${device.id}`} className={`${disabled} ${isActive} ${gate} device-gui device-gui--central-mixer device-${error} ${inputDisabled}`}>

        <Sound file={Sounds.horn} play={mixerDataStatus.restart} />
        {device.analogInputId && (
          <IconUnitLabel value={batchStore.getAnalogInputValue(device.analogInputId)}
            unit={analogInput.unit || AnalogUnitList['%']}
            icon={analogInput.icon || AnalogIconList.DROP}/>
        )}
        <div className="device-label">
          <div className="device-type" title={`#${device.id} ${inputDisabled}`}>
            {inputDisabled ? <i className="fa fa-warning"/> : ''} {name}
          </div>
          <div className="status">{mixerDataStatus.status}</div>
          <div className="device-timer">
            {
              batchStore.replayMode
                ? formatSeconds(mixerDataStatus.currentCount)
                :
                (
                  <Counter delay={mixerDataStatus.delay}
                    currentCount={mixerDataStatus.currentCount}
                    onChange={onCountDown}
                    start={countDownOn}
                    restart={mixerDataStatus.restart}
                  />
                )
            }
          </div>
          {mCode && <div className="mix" title={mCode}>{mCode}</div>}
          {device.mixerOnPNo && (
            <NormalButton className={`btn-on btn-batch ${isOn ? 'isActive btn-batch-red' : 'btn-batch-green'}`}
              onClick={() => {
                mixerSwitch(batchStore, device, !isOn);
                batchStore.rampUpMixer(device.id, !isOn);
              }}
            >
              <i className="fa fa-power-off"/>
            </NormalButton>
          )}
        </div>
        <LockIcon
          lockModeEnabled={lockModeEnabled}
          onToggle={this.handleDeviceLockToggle}
          className="central-mixer-lock-icon"
        />
        <div className="device-icon support"/>
        <div className="device-icon barrel-wrap">
          <div className="device-icon barrel barrel-1"/>
          <div className="device-icon barrel barrel-2"/>
          <div className="device-icon barrel barrel-3"/>
        </div>
        <div className="device-gui__row device-gui__row--central-mixer">
          {!batchStore.running.length && device.diverterPNo && (
            <div className="toggle-disable">
              <Toggle checked={!isDisabled} onChange={() => {
                batchStore.setOutput(device.diverterPNo, !isDisabled ? OUTPUT_TTL : '0');
              }}/>
            </div>
          )}
          <LockableButtonGroup
            id={`central-mixer-${device.id}`}
            upButtonPort={device.closeReturnPNo}
            downButtonPort={device.openTiltPNo}
            upLimitReached={isBarrelReturned}
            downLimitReached={isBarrelFullyTilted}
            onLockStateChange={this.handleLockStateChange}
            downButtonDisabled={batchStore.mixerDownButtonShouldBeDisabled}
          />
        </div>
      </div>
    );
  }
}

export default CentralMixer;
